import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  WheelEvent as ReactWheelEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import { ArrowLeft, Check, Loader, Maximize, MessageSquare, Mic, Minimize, Plus, StopCircle, Trash2 } from 'react-feather';
import {
  executeBotServiceConnection,
  generateUserActivityInstructions,
  getUserActivityWsUrl,
  saveUserActivityInstructions,
  startUserActivityStream,
  uploadUserActivityFile,
} from './store';

interface ActivityLog {
  message: string;
  timestamp: string;
  type?: string;
  action?: string;
  tag?: string;
  id?: string;
  class?: string;
  value?: string;
  className?: string;
  xpath?: string;
  inputType?: string;
  placeholder?: string;
  description?: string;
  Detailed_description?: string;
}

interface InstructionStepDetail {
  action: string;
  description: string;
  Detailed_description?: string;
  className?: string;
  id?: string;
  tag?: string;
  xpath?: string;
  input_type?: string;
  placeholder?: string;
  /** @deprecated legacy mutually-exclusive field; kept for reading older recordings only */
  step_type?: 'optional' | 'list' | '';
  selection_strategy?: 'list' | '';
  optional?: boolean;
  /** Optional human-authored instruction telling IPA how to handle this step
   * dynamically at execution time (e.g. "this will show a patient list —
   * select the one matching the current Member ID"). */
  dynamic_instruction?: string;
}

interface MousePosition {
  x: number;
  y: number;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

const UserActivityStream = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [projectTitle, setProjectTitle] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [instructionSteps, setInstructionSteps] = useState<InstructionStepDetail[]>([]);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [isGeneratingInstructions, setIsGeneratingInstructions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingInstructionIndex, setEditingInstructionIndex] = useState<number | null>(null);
  const [editingInstructionText, setEditingInstructionText] = useState('');
  const [dynamicInstructionOpenSteps, setDynamicInstructionOpenSteps] = useState<Set<number>>(new Set());
  const [isInstructionRecording, setIsInstructionRecording] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [cursorStyle, setCursorStyle] = useState('default');
  const [navUrl, setNavUrl] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedFileType, setUploadedFileType] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fsContainerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const recognitionBaseTextRef = useRef('');

  const userId = useMemo(() => localStorage.getItem('id') || '', []);
  const username = useMemo(() => localStorage.getItem('UserName') || '', []);
  const useremail = useMemo(() => localStorage.getItem('emailId') || localStorage.getItem('Email') || localStorage.getItem('UserEmail') || '', []);

  const safeString = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') return value || 'N/A';
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      try {
        const stringified = JSON.stringify(value);
        return Object.keys(value).length === 0 ? 'N/A' : stringified;
      } catch {
        return 'N/A';
      }
    }
    return 'N/A';
  };

  const removeActivityLog = useCallback((index: number) => {
    setActivityLogs((previous) => previous.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const displayScreenshot = (base64Image: string) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const image = new Image();
    image.onload = () => {
      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);
    };
    image.src = base64Image;
  };

  const sendCommand = useCallback((command: object) => {
    if (wsRef.current && wsConnected) {
      wsRef.current.send(JSON.stringify(command));
    }
  }, [wsConnected]);

  const getScaledCoords = useCallback((event: ReactMouseEvent<HTMLCanvasElement>): MousePosition => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: Math.round((event.clientX - rect.left) * scaleX),
      y: Math.round((event.clientY - rect.top) * scaleY),
    };
  }, []);

  const stopStreaming = useCallback(async (force = false) => {
    if (!isStreaming && !force) return;

    setMessage('⏹️ Stopping browser...');
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
    }

    document.documentElement.style.overflow = '';
    setIsExpanded(false);
    setIsStreaming(false);
    setWsConnected(false);
    setNavUrl('');
    setMessage('✅ Browser stopped.');
    setTimeout(() => setMessage(''), 3000);
  }, [isStreaming]);

  const handleToggleExpand = useCallback(async () => {
    const element = fsContainerRef.current;
    if (!element) return;

    try {
      if (!document.fullscreenElement) {
        await element.requestFullscreen();
        document.documentElement.style.overflow = 'hidden';
        setIsExpanded(true);
      } else {
        await document.exitFullscreen();
        document.documentElement.style.overflow = '';
        setIsExpanded(false);
      }
    } catch (error) {
      console.error('Fullscreen toggle failed', error);
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      const expanded = document.fullscreenElement === fsContainerRef.current;
      setIsExpanded(expanded);
      if (!expanded) {
        document.documentElement.style.overflow = '';
      }
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [activityLogs]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = '';
      for (let index = 0; index < event.results.length; index += 1) {
        transcript += event.results[index][0].transcript;
      }
      const baseText = recognitionBaseTextRef.current;
      const separator = baseText && transcript ? ' ' : '';
      setEditingInstructionText(baseText + separator + transcript);
    };
    recognition.onerror = () => {
      setIsInstructionRecording(false);
    };
    recognition.onend = () => {
      setIsInstructionRecording(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (!sessionId || !isStreaming) return;

    let isCancelled = false;
    let socket: WebSocket | null = null;

    const connectWebSocket = async () => {
      const wsUrl = await getUserActivityWsUrl(sessionId, url, userId);
      if (isCancelled) return;

      socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        setWsConnected(true);
        setMessage('🎥 Live browser stream connected');
      };

      socket.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'screenshot') {
            if (data.cursor) setCursorStyle(data.cursor);
            displayScreenshot(data.image);
            if (data.navigation) {
              if (typeof data.navigation.url === 'string') {
                setNavUrl(data.navigation.url);
              }
            }
            return;
          }

          if (data.type === 'upload_status') {
            setMessage('📁 File uploaded successfully!');
            return;
          }

          if (data.type === 'activity_log') {
            if (data.action_type === 'click') {
              const dynamicXpath = data.raw?.closestFileInputXpath;
              if (dynamicXpath && dynamicXpath !== 'null') {
                const picker = document.createElement('input');
                picker.type = 'file';
                picker.onchange = async (changeEvent: Event) => {
                  const target = changeEvent.target as HTMLInputElement;
                  const file = target.files?.[0];
                  if (!file) return;

                  setUploadedFileName(file.name);
                  setUploadedFileType(file.type || file.name.split('.').pop() || 'unknown');

                  const uploadResponse = await uploadUserActivityFile(file);
                  wsRef.current?.send(
                    JSON.stringify({
                      action: 'inject_file',
                      xpath: dynamicXpath,
                      path: uploadResponse.path,
                    })
                  );
                };
                picker.click();
              }
            }

            setActivityLogs((previousLogs) => [
              ...previousLogs,
              {
                message: safeString(data.message),
                timestamp: data.timestamp || new Date().toLocaleTimeString(),
                type: safeString(data.type),
                action: safeString(data.action_type),
                tag: safeString(data.tag),
                id: safeString(data.id),
                class: safeString(data.className),
                value: safeString(data.value || data.selectedValue),
                className: safeString(data.className),
                xpath: safeString(data.xpath),
                inputType: safeString(data.input_type),
                placeholder: safeString(data.placeholder),
                description: safeString(data.message),
                Detailed_description: safeString(data.Detailed_description),
              },
            ]);
            return;
          }

          if (data.type === 'tab_switched') {
            if (typeof data.url === 'string') {
              setNavUrl(data.url);
            }
            return;
          }

          if (data.type === 'clipboard_data') {
            await navigator.clipboard.writeText(data.text);
            return;
          }

          if (data.type === 'error') {
            setMessage(`❌ ${data.message}`);
            stopStreaming(true);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      socket.onclose = () => {
        if (isStreaming) {
          setMessage('⏹️ Stream disconnected.');
        }
        setWsConnected(false);
      };

      socket.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        setMessage('❌ WebSocket connection error.');
        setWsConnected(false);
      };
    };

    connectWebSocket().catch((error) => {
      console.error('Failed to connect user activity websocket:', error);
      setMessage(`❌ Failed to connect stream: ${error.message || 'Unknown error'}`);
      setWsConnected(false);
    });

    return () => {
      isCancelled = true;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
      wsRef.current = null;
    };
  }, [isStreaming, sessionId, stopStreaming, url, userId]);

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const text = event.clipboardData?.getData('text');
      if (text) {
        sendCommand({ action: 'paste', text });
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [sendCommand]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (editingInstructionIndex === null) return;

    setInstructionSteps((previousSteps) => {
      if (!previousSteps[editingInstructionIndex]) return previousSteps;
      if (previousSteps[editingInstructionIndex].description === editingInstructionText) return previousSteps;

      const nextSteps = [...previousSteps];
      nextSteps[editingInstructionIndex] = {
        ...nextSteps[editingInstructionIndex],
        description: editingInstructionText,
      };
      return nextSteps;
    });
  }, [editingInstructionIndex, editingInstructionText]);

  const handleMouseMove = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getScaledCoords(event);
    sendCommand({ action: 'mousemove', x, y });
  };

  const handleCanvasClick = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getScaledCoords(event);
    sendCommand({ action: 'click', x, y });
  };

  const handleWheel = (event: ReactWheelEvent<HTMLCanvasElement>) => {
    sendCommand({ action: 'scroll', deltaY: Math.round(event.deltaY) });
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLCanvasElement>) => {
    if (event.ctrlKey && event.key.toLowerCase() === 'c') {
      event.preventDefault();
      sendCommand({ action: 'copy' });
      return;
    }

    if (event.ctrlKey && event.key.toLowerCase() === 'v') return;
    if (event.metaKey && event.key.toLowerCase() === 'v') return;

    if (event.key.length === 1) {
      sendCommand({ action: 'type', text: event.key });
      return;
    }

    if (event.key.match(/^(Arrow|Backspace|Delete|Enter|Tab|Escape)/)) {
      sendCommand({ action: 'keypress', key: event.key });
    }
  };

  const handleStartStreaming = async () => {
    if (isStreaming) return;

    if (!url || !projectTitle) {
      setMessage('⚠️ Please enter URL and Bot Name');
      return;
    }

    if (!userId || !username) {
      setMessage('⚠️ Missing user information in local storage.');
      return;
    }

    try {
      setMessage('🚀 Starting stream...');
      setActivityLogs([]);
      const nextSessionId = `stream_${Date.now()}`;
      await executeBotServiceConnection(userId);
      await startUserActivityStream({
        url,
        project_title: projectTitle,
        session_id: nextSessionId,
        userid: userId,
        username,
      });
      setSessionId(nextSessionId);
      setIsStreaming(true);
      setTimeout(() => {
        handleToggleExpand();
      }, 500);
    } catch (error: any) {
      setMessage(`❌ Failed to start: ${error.message || 'Unknown error'}`);
    }
  };

  // Derives the two independent behaviour flags from raw step data, falling back to
  // the legacy mutually-exclusive `step_type` field ("optional"/"list"/"") for steps
  // saved before selection_strategy/optional existed separately — so a step can be
  // both list-type AND optional (e.g. an extra-coverage row that isn't always present).
  const deriveStepBehavior = (stepVal: any): { selection_strategy: 'list' | ''; optional: boolean; dynamic_instruction: string } => {
    const legacy = stepVal?.step_type;
    const selection_strategy: 'list' | '' =
      stepVal?.selection_strategy === 'list' ? 'list' : legacy === 'list' ? 'list' : '';
    const optional: boolean =
      typeof stepVal?.optional === 'boolean' ? stepVal.optional : legacy === 'optional';
    const dynamic_instruction: string =
      typeof stepVal?.dynamic_instruction === 'string' ? stepVal.dynamic_instruction : '';
    return { selection_strategy, optional, dynamic_instruction };
  };

  const normalizeInstructions = (rawInstructions: any): InstructionStepDetail[] => {
    let instructions: InstructionStepDetail[] = [];
    let parsedInstructions = rawInstructions;

    if (typeof parsedInstructions === 'object' && parsedInstructions !== null && !Array.isArray(parsedInstructions)) {
      instructions = Object.entries(parsedInstructions).map(([stepKey, stepVal]: [string, any]) => {
        const logIndex = stepVal.original_log_index;
        let detailedDesc = '';
        if (logIndex !== null && logIndex !== undefined && logIndex !== "N/A") {
          const idx = parseInt(logIndex, 10);
          if (!isNaN(idx) && idx >= 0 && idx < activityLogs.length) {
            detailedDesc = activityLogs[idx].Detailed_description || '';
          }
        }
        if (stepKey === "Step 1" || stepVal.action === "Open website") {
          detailedDesc = stepVal.description;
        }
        return {
          ...stepVal,
          Detailed_description: detailedDesc || stepVal.Detailed_description || stepVal.description || '',
          ...deriveStepBehavior(stepVal),
        };
      }) as InstructionStepDetail[];
    }

    if (Array.isArray(parsedInstructions)) {
      if (parsedInstructions.length > 0 && typeof parsedInstructions[0] === 'string') {
        const firstItem = parsedInstructions[0].trim();
        if (firstItem.startsWith('```') || firstItem.startsWith('[')) {
          try {
            const cleanJson = firstItem
              .replace(/^```json\s*/i, '')
              .replace(/^```\s*/i, '')
              .replace(/\s*```$/, '');
            const parsed = JSON.parse(cleanJson);
            if (Array.isArray(parsed)) {
              parsedInstructions = parsed;
            }
          } catch {
          }
        }
      }

      if (parsedInstructions.length > 0) {
        if (typeof parsedInstructions[0] === 'object' && parsedInstructions[0] !== null) {
          instructions = parsedInstructions.map((stepVal: any) => {
            const logIndex = stepVal.original_log_index;
            let detailedDesc = '';
            if (logIndex !== null && logIndex !== undefined && logIndex !== "N/A") {
              const idx = parseInt(logIndex, 10);
              if (!isNaN(idx) && idx >= 0 && idx < activityLogs.length) {
                detailedDesc = activityLogs[idx].Detailed_description || '';
              }
            }
            if (stepVal.action === "Open website") {
              detailedDesc = stepVal.description;
            }
            return {
              ...stepVal,
              Detailed_description: detailedDesc || stepVal.Detailed_description || stepVal.description || '',
              ...deriveStepBehavior(stepVal),
            };
          }) as InstructionStepDetail[];
        } else if (typeof parsedInstructions[0] === 'string') {
          instructions = parsedInstructions.map((item: string, idx: number) => {
            let detailedDesc = '';
            if (idx < activityLogs.length) {
              detailedDesc = activityLogs[idx].Detailed_description || '';
            }
            return {
              description: item,
              action: 'N/A',
              selection_strategy: '' as const,
              optional: false,
              dynamic_instruction: '',
              className: 'N/A',
              id: 'N/A',
              tag: 'N/A',
              xpath: 'N/A',
              input_type: 'N/A',
              placeholder: 'N/A',
              Detailed_description: detailedDesc || item || '',
            };
          });
        }
      }
    }

    return instructions.map((step) => ({
      action: safeString(step.action),
      description: safeString(step.description),
      Detailed_description: safeString(step.Detailed_description || step.description),
      className: safeString(step.className),
      id: safeString(step.id),
      tag: safeString(step.tag),
      xpath: safeString(step.xpath),
      input_type: safeString(step.input_type === 'N/A' ? null : step.input_type),
      placeholder: safeString(step.placeholder === 'N/A' ? null : step.placeholder),
      selection_strategy: step.selection_strategy === 'list' ? 'list' as const : '' as const,
      optional: !!step.optional,
      dynamic_instruction: typeof step.dynamic_instruction === 'string' ? step.dynamic_instruction : '',
    }));
  };

  const handleGenerateInstruction = async () => {
    if (!activityLogs.length || !sessionId) {
      setMessage('⚠️ No activity logs or session not active.');
      return;
    }

    // Exit fullscreen mode when Generate Instruction is clicked
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        document.documentElement.style.overflow = '';
        setIsExpanded(false);
      }
    } catch (error) {
      console.error('Failed to exit fullscreen:', error);
    }

    setIsGeneratingInstructions(true);
    setInstructionSteps([]);
    setShowInstructionModal(true);
    setMessage('');

    try {
      const response = await generateUserActivityInstructions(userId, {
        url,
        userId,
        sessionId,
        projectTitle,
        instruction: activityLogs.map((log: ActivityLog) => log.message),
        xpath: activityLogs.map((log: ActivityLog) => log.xpath || 'N/A'),
        tags: activityLogs.map((log: ActivityLog) => log.tag || 'N/A'),
        class_name: activityLogs.map((log: ActivityLog) => log.class || 'N/A'),
        id: activityLogs.map((log: ActivityLog) => log.id || 'N/A'),
        input_type: activityLogs.map((log: ActivityLog) => log.inputType || 'N/A'),
        placeholder: activityLogs.map((log: ActivityLog) => log.placeholder || 'N/A'),
        file_name: uploadedFileName,
        file_type: uploadedFileType,
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to generate instructions');
      }

      setInstructionSteps(normalizeInstructions(response.instruction));
      setMessage('✅ Instructions generated successfully!');
    } catch (error: any) {
      setMessage(`❌ Failed to generate instructions: ${error.message || 'Server error'}`);
      setShowInstructionModal(false);
    } finally {
      setIsGeneratingInstructions(false);
    }
  };

  const handleSaveInstructions = async () => {
    if (!userId || !username || !sessionId || !projectTitle) {
      setMessage('⚠️ Cannot save. Missing required information.');
      return;
    }

    try {
      setIsSaving(true);
      setMessage('💾 Saving instructions...');

      // Track placeholder occurrences to make duplicates unique
      const placeholderCounts: Record<string, number> = {};
      const placeholderMapping: Record<string, string> = {};

      // First pass: identify all placeholders and their occurrences
      instructionSteps.forEach((step) => {
        const matches = step.description.match(/\{([^}]+)\}/g);
        if (matches) {
          matches.forEach((match) => {
            const placeholder = match.slice(1, -1); // Remove { }
            placeholderCounts[placeholder] = (placeholderCounts[placeholder] || 0) + 1;
          });
        }
      });

      // Second pass: create unique names for duplicate placeholders
      const usedPlaceholders: Record<string, number> = {};
      const processedSteps = instructionSteps.map((step) => {
        let processedDescription = step.description;
        const matches = step.description.match(/\{([^}]+)\}/g);

        if (matches) {
          matches.forEach((match) => {
            const placeholder = match.slice(1, -1);

            // If this placeholder appears multiple times across steps
            if (placeholderCounts[placeholder] > 1) {
              usedPlaceholders[placeholder] = (usedPlaceholders[placeholder] || 0) + 1;
              const uniqueName = `${placeholder}_${usedPlaceholders[placeholder]}`;

              // Replace only the first occurrence in this step
              processedDescription = processedDescription.replace(match, `{${uniqueName}}`);
              placeholderMapping[uniqueName] = placeholder;
            }
          });
        }

        return {
          ...step,
          description: processedDescription,
        };
      });

      const instruction = processedSteps.reduce((accumulator, step, index) => {
        accumulator[`Step ${index + 1}`] = step;
        return accumulator;
      }, {} as Record<string, InstructionStepDetail>);

      const steps = processedSteps.map((step, index) => ({
        step: index + 1,
        action: safeString(step.action),
        prompt: safeString(step.description),
        Detailed_description: safeString(step.Detailed_description || step.description),
        selection_strategy: step.selection_strategy === 'list' ? 'list' as const : '' as const,
        optional: !!step.optional,
        dynamic_instruction: typeof step.dynamic_instruction === 'string' ? step.dynamic_instruction : '',
        element: {
          id: safeString(step.id),
          class: safeString(step.className),
          tagname: safeString(step.tag),
          xpath: safeString(step.xpath),
          input_type: safeString(step.input_type),
          placeholder: safeString(step.placeholder),
        },
      }));

      await saveUserActivityInstructions({
        userId,
        username,
        useremail,
        sessionId,
        projectTitle,
        url,
        description,
        instruction,
        steps,
      });

      setMessage('✅ Instructions saved successfully!');
      setShowInstructionModal(false);
      navigate('/bot-manager');
    } catch (error: any) {
      setMessage(`❌ Failed to save instructions: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDescriptionChange = (index: number, value: string) => {
    setInstructionSteps((previous) => previous.map((step, currentIndex) => (
      currentIndex === index ? { ...step, description: value } : step
    )));
  };

  const handleOptionalToggle = (index: number, checked: boolean) => {
    setInstructionSteps((previous) => previous.map((step, currentIndex) => (
      currentIndex === index ? { ...step, optional: checked } : step
    )));
  };

  const handleDynamicInstructionChange = (index: number, value: string) => {
    setInstructionSteps((previous) => previous.map((step, currentIndex) => (
      currentIndex === index ? { ...step, dynamic_instruction: value } : step
    )));
  };

  const toggleDynamicInstructionEditor = (index: number) => {
    setDynamicInstructionOpenSteps((previous) => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleToggleInstructionRecording = (index: number) => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      setMessage('⚠️ Speech recognition is not supported in this browser.');
      return;
    }

    if (!isInstructionRecording) {
      recognitionBaseTextRef.current = instructionSteps[index]?.description || '';
      setEditingInstructionIndex(index);
      recognition.start();
      setIsInstructionRecording(true);
      return;
    }

    recognition.stop();
    setIsInstructionRecording(false);
  };

  const handleInsertInstruction = (index: number) => {
    const nextSteps = [...instructionSteps];
    nextSteps.splice(index, 0, {
      description: '',
      action: '',
      className: 'N/A',
      id: 'N/A',
      tag: 'N/A',
      xpath: 'N/A',
      input_type: 'N/A',
      placeholder: 'N/A',
      selection_strategy: '',
      optional: false,
      dynamic_instruction: '',
    });
    setInstructionSteps(nextSteps);
    setEditingInstructionIndex(index);
    setEditingInstructionText('');
  };

  const handleAddStepOption = (index: number) => {
    Swal.fire({
      target: fsContainerRef.current || document.body,
      title: 'Add Step',
      text: 'Where do you want to insert the new step?',
      icon: 'question',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Insert Before',
      denyButtonText: 'Insert After',
      confirmButtonColor: '#3b82f6',
      denyButtonColor: '#3b82f6',
    }).then((result) => {
      if (result.isConfirmed) {
        handleInsertInstruction(index);
      } else if (result.isDenied) {
        handleInsertInstruction(index + 1);
      }
    });
  };

  const handleAddInstruction = () => {
    const nextSteps: InstructionStepDetail[] = [
      ...instructionSteps,
      {
        description: '',
        action: '',
        className: 'N/A',
        id: 'N/A',
        tag: 'N/A',
        xpath: 'N/A',
        input_type: 'N/A',
        placeholder: 'N/A',
        selection_strategy: '',
        optional: false,
        dynamic_instruction: '',
      },
    ];
    setInstructionSteps(nextSteps);
    setEditingInstructionIndex(nextSteps.length - 1);
    setEditingInstructionText('');
  };

  const handleDeleteInstruction = (index: number) => {
    Swal.fire({
      target: fsContainerRef.current || document.body,
      title: 'Delete this step?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        setInstructionSteps((previousSteps) => previousSteps.filter((_, currentIndex) => currentIndex !== index));
      }
    });
  };

  return (
    <div className={`min-vh-100 ${isExpanded ? 'p-0' : ''}`}>
      <Card className="border-0 shadow-sm mb-3">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="mb-1">User Activity Stream</h3>
              <div className="text-muted small">Control remote browser in real-time</div>
            </div>
            <Button
              color="light"
              size="sm"
              className="d-flex align-items-center gap-2 px-4 shadow-sm border"
              onClick={() => navigate(-1)}
              style={{ borderRadius: '8px', fontWeight: 600, height: '40px' }}
            >
              <ArrowLeft size={16} /> Back
            </Button>
          </div>

          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Label>Target URL <span className="text-danger">*</span></Label>
              <Input
                type="text"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                onBlur={(event) => {
                  const value = event.target.value.trim();
                  if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                    setUrl(`https://${value}`);
                  }
                }}
                placeholder="https://www.example.com"
                disabled={isStreaming}
              />
            </Col>
            <Col md={5}>
              <Label>Bot Name <span className="text-danger">*</span></Label>
              <Input
                type="text"
                value={projectTitle}
                onChange={(event) => setProjectTitle(event.target.value)}
                placeholder="Enter bot name"
                disabled={isStreaming}
              />
            </Col>
            <Col md={2}>
              <Button
                color={isStreaming ? 'danger' : 'primary'}
                className="w-100"
                onClick={isStreaming ? () => stopStreaming() : handleStartStreaming}
                disabled={!isStreaming && (!url.trim() || !projectTitle.trim())}
              >
                {isStreaming ? 'Stop Stream' : 'Start Stream'}
              </Button>
            </Col>
            <Col md={12}>
              <Label>Assistant Description</Label>
              <Input
                type="textarea"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Enter a description for your assistant"
                disabled={isStreaming}
              />
            </Col>
          </Row>

          {message ? (
            <div
              className="mt-3 px-3 py-2 rounded"
              style={{
                backgroundColor: '#eff6ff',
                borderLeft: '4px solid #3b82f6',
              }}
            >
              <div style={{ fontSize: '13px', color: '#1e40af' }}>{message}</div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <div ref={fsContainerRef}>
        <Row className="g-3">
          <Col xl={9}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">Browser View</h5>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {/* <Button color="light" disabled={!wsConnected || !canGoBack} onClick={() => sendCommand({ action: 'back' })}>Back</Button>
                    <Button color="light" disabled={!wsConnected || !canGoForward} onClick={() => sendCommand({ action: 'forward' })}>Forward</Button>
                    <Button color="light" disabled={!wsConnected} onClick={() => sendCommand({ action: 'refresh' })}>Refresh</Button> */}
                    <Button color="light" onClick={handleToggleExpand}>
                      {isExpanded ? <Minimize size={16} /> : <Maximize size={16} />}
                    </Button>
                  </div>
                </div>

                <div
                  style={{
                    background: '#111827',
                    borderRadius: '10px',
                    minHeight: isExpanded ? 'calc(100vh - 180px)' : '650px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    tabIndex={0}
                    onMouseMove={handleMouseMove}
                    onClick={handleCanvasClick}
                    onWheel={handleWheel}
                    onKeyDown={handleKeyDown}
                    style={{
                      maxWidth: '100%',
                      width: '100%',
                      height: '100%',
                      cursor: cursorStyle,
                      outline: 'none',
                    }}
                  />
                  {!isStreaming ? <div className="text-white-50 position-absolute">No active stream</div> : null}
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex flex-column h-100 p-3 p-xl-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <h5 className="mb-0">Activity Log</h5>
                  <span className="text-muted small">{activityLogs.length} entries</span>
                </div>
                <div
                  ref={logContainerRef}
                  className="flex-grow-1 w-100"
                  style={{
                    minHeight: isExpanded ? 'calc(100vh - 280px)' : '580px',
                    maxHeight: isExpanded ? 'calc(100vh - 280px)' : '580px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: '1px solid #e9ecef',
                    borderRadius: '10px',
                    padding: '12px',
                    background: '#f8f9fa',
                    width: '100%',
                    marginBottom: '16px'
                  }}
                >
                  {activityLogs.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center h-100 text-center text-muted px-3">Waiting for user activity...</div>
                  ) : (
                    activityLogs.map((log, index) => (
                      <Card key={`${log.timestamp}-${index}`} className="border shadow-sm mb-3">
                        <CardBody className="p-3">
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div className="d-flex align-items-start gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                              <div
                                className="d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  width: 26,
                                  height: 26,
                                  borderRadius: 999,
                                  background: '#2563eb',
                                  color: '#fff',
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                {index + 1}
                              </div>

                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div
                                  className="px-3 py-2 rounded"
                                  style={{
                                    background: '#eff6ff',
                                    borderLeft: '3px solid #2563eb',
                                    fontSize: 13,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  {log.message}
                                </div>
                              </div>
                            </div>

                            <Button
                              color="link"
                              className="p-0 text-danger"
                              style={{ lineHeight: 1 }}
                              onClick={() => removeActivityLog(index)}
                              title="Remove"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>

                          <div className="mt-3" style={{ fontSize: 12 }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted">Type:</span>
                              <span className="px-2 py-1 rounded" style={{ background: '#eef2ff', color: '#1f2937', minWidth: 140, textAlign: 'left' }}>
                                {safeString(log.type)}
                              </span>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted">Action:</span>
                              <span className="px-2 py-1 rounded" style={{ background: '#ecfdf5', color: '#065f46', minWidth: 140, textAlign: 'left' }}>
                                {safeString(log.action)}
                              </span>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted">Tag:</span>
                              <span style={{ minWidth: 140, textAlign: 'left' }}>{safeString(log.tag)}</span>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted">ID:</span>
                              <span style={{ minWidth: 140, textAlign: 'left' }}>{safeString(log.id)}</span>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted">Class:</span>
                              <span style={{ minWidth: 140, textAlign: 'left' }}>{safeString(log.class)}</span>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted">Value:</span>
                              <span className="px-2 py-1 rounded" style={{ background: '#fffbeb', color: '#92400e', minWidth: 140, textAlign: 'left' }}>
                                {safeString(log.value)}
                              </span>
                            </div>

                            <div className="d-flex align-items-start justify-content-between">
                              <span className="text-muted">Description:</span>
                              <span className="px-2 py-1 rounded" style={{ background: '#f3f4f6', color: '#374151', minWidth: 140, maxWidth: 140, textAlign: 'left', wordBreak: 'break-word', fontSize: '11px' }}>
                                {safeString(log.Detailed_description)}
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  )}
                </div>

                <Button
                  color="primary"
                  className="w-100 d-flex align-items-center justify-content-center gap-2 py-2.5 shadow mt-auto"
                  style={{
                    background: '#4f46e5',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '13px',
                  }}
                  onClick={handleGenerateInstruction}
                  disabled={isGeneratingInstructions || activityLogs.length === 0}
                >
                  {isGeneratingInstructions ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                      <path d="m5 3 1 2.5L8.5 6 6 7 5 9.5 4 7 1.5 6 4 5z"/>
                      <path d="m19 17 1 2.5 2.5.5-2.5 1-1 2.5-1 2.5-2.5-1 2.5-1z"/>
                    </svg>
                  )}
                  Generate Instruction
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal isOpen={showInstructionModal} toggle={() => setShowInstructionModal(false)} size="lg" centered backdrop="static">
        <ModalHeader toggle={() => setShowInstructionModal(false)}>Generated Instructions</ModalHeader>
        <ModalBody className="position-relative p-0">
          {(isSaving || isGeneratingInstructions) && (
            <div
              className="position-absolute w-100 h-100 d-flex flex-column align-items-center justify-content-center"
              style={{
                top: 0,
                left: 0,
                background: '#ffffff',
                zIndex: 1000,
                borderRadius: '0 0 12px 12px'
              }}
            >
              <Spinner color="primary" />
              <div className="mt-3 fw-bold text-primary" style={{ fontSize: '16px' }}>
                {isSaving ? 'Saving Instructions...' : 'Generating Instructions...'}
              </div>
            </div>
          )}

          <div className="p-4" style={{ background: '#f8fafc', maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="w-100">
              <div className="d-flex flex-column align-items-stretch" style={{ gap: 14 }}>
                {instructionSteps.map((step, index) => (
                  <div key={`instruction-${index}`} className="d-flex gap-3 align-items-start w-100">
                    {/* Step Number Badge */}
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: '#3b82f6',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 14,
                        marginTop: 4,
                      }}
                    >
                      {index + 1}
                    </div>

                    {/* Step Content */}
                    <div className="flex-grow-1 w-100">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: '#ffffff',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            width: '100%'
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="position-relative mb-2">
                                <Input
                                  type="textarea"
                                  rows={2}
                                  value={step.description}
                                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                  className="pe-5"
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: '#374151',
                                    borderRadius: 6,
                                    resize: 'vertical',
                                  }}
                                />
                                <button
                                  className="btn btn-link p-1 position-absolute text-muted"
                                  style={{ right: '8px', bottom: '8px' }}
                                  onClick={() => handleToggleInstructionRecording(index)}
                                  title={isInstructionRecording && editingInstructionIndex === index ? 'Stop recording' : 'Voice input'}
                                >
                                  {isInstructionRecording && editingInstructionIndex === index
                                    ? <StopCircle size={18} className="text-danger" />
                                    : <Mic size={18} />}
                                </button>
                              </div>
                              {/* Badges for input_type and placeholder */}
                              <div className="d-flex gap-2 flex-wrap">
                                {step.input_type && step.input_type !== 'N/A' && (
                                  <span
                                    className="px-2 py-1 rounded"
                                    style={{
                                      background: '#f3e8ff',
                                      color: '#7c3aed',
                                      fontSize: 12,
                                    }}
                                  >
                                    Type: {step.input_type}
                                  </span>
                                )}
                                {step.placeholder && step.placeholder !== 'N/A' && (
                                  <span
                                    className="px-2 py-1 rounded"
                                    style={{
                                      background: '#cffafe',
                                      color: '#0e7490',
                                      fontSize: 12,
                                    }}
                                  >
                                    Placeholder: {step.placeholder}
                                  </span>
                                )}
                              </div>
                              <div className="mt-2 d-flex gap-3 align-items-center flex-wrap">
                                <div
                                  className="d-flex align-items-center gap-2"
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleOptionalToggle(index, !step.optional)}
                                >
                                  <div
                                    style={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: 4,
                                      border: step.optional ? '2px solid #2563eb' : '2px solid #6b7280',
                                      backgroundColor: step.optional ? '#2563eb' : '#ffffff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.15s ease-in-out',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {step.optional && (
                                      <Check size={14} color="#ffffff" strokeWidth={3} />
                                    )}
                                  </div>
                                  <span className="small fw-semibold text-secondary mb-0" style={{ fontSize: '13px' }}>
                                    Optional step
                                  </span>
                                </div>
                              </div>
                              {(dynamicInstructionOpenSteps.has(index) || !!step.dynamic_instruction) && (
                                <div className="mt-2">
                                  <Label className="small fw-semibold text-muted mb-1">
                                    Dynamic Instruction (tells IPA how to handle this step at execution time)
                                  </Label>
                                  <Input
                                    type="textarea"
                                    rows={2}
                                    bsSize="sm"
                                    placeholder="e.g. This step will show a patient list — select the one matching the current Member ID."
                                    value={step.dynamic_instruction || ''}
                                    onChange={(e) => handleDynamicInstructionChange(index, e.target.value)}
                                    style={{ borderRadius: 6, fontSize: 13 }}
                                  />
                                </div>
                              )}
                            </div>
                            {/* Action Buttons */}
                            <div className="d-flex gap-1 ms-2">
                              <button
                                className={`btn btn-link p-1 ${step.dynamic_instruction ? 'text-primary' : 'text-muted'}`}
                                onClick={() => toggleDynamicInstructionEditor(index)}
                                title="Add Dynamic Instruction"
                              >
                                <MessageSquare size={16} />
                              </button>
                              <button
                                className="btn btn-link p-1 text-muted"
                                onClick={() => handleAddStepOption(index)}
                                title="Insert Step"
                              >
                                <Plus size={16} />
                              </button>
                              <button
                                className="btn btn-link p-1 text-danger"
                                onClick={() => handleDeleteInstruction(index)}
                                title="Delete Step"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                    </div>
                  </div>
                ))}

                {/* Add Step Button */}
                <button
                  className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    borderRadius: 8,
                    padding: '12px 16px',
                    background: '#eff6ff',
                    border: '1px solid #dbeafe',
                    color: '#2563eb',
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                  onClick={handleAddInstruction}
                >
                  <Plus size={16} /> Add Step
                </button>
              </div>
            </div>
          </div>
        </ModalBody>
        <div className="d-flex justify-content-end gap-2 p-3 pt-0" style={{ background: '#ffffff' }}>
          <Button
            color="light"
            style={{ minWidth: 110 }}
            onClick={() => setShowInstructionModal(false)}
            disabled={isSaving || isGeneratingInstructions}
          >
            Close
          </Button>
          <Button
            color="primary"
            style={{ minWidth: 150 }}
            onClick={handleSaveInstructions}
            disabled={isSaving || isGeneratingInstructions || instructionSteps.length === 0}
          >
            {isSaving ? <Loader size={16} className="me-2" /> : null}
            Save Instructions bot
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default UserActivityStream;
